// MUI Components
import { alpha, Box, Button, Skeleton, Theme, Typography, useTheme } from "@mui/material";

// Components
import { Body, Header } from "components/Layout/LayoutContent";
import StatusCard from "components/Pace/StatusCard";
import { StatusCardHeader } from "components/Pace/StatusCardHeader";
import CustomIcon from "components/CustomIcon";
import { LeadershipSnapshotCard } from "./components";
import { BehaviouralBreakdown } from "components/Charts";
import { BBCompletePaceError } from "components/Charts/BehaviouralBreakdown/ErrorMessages";
import YourProfile from "components/PaceLabsDashboard/YourProfile/YourProfile";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

// Hooks
import useMyDashboard from "./useMyDashboard";

// Utils
import icons from "enums/icons";

// Types
import { RootState } from "app/store";

const MyDashboard = () => {
    const intl = useIntl();
    const theme = useTheme<Theme>();

    const {
        person_id,
        reportStatus,
        chartData,
        statusBarColor,
        retakeAvailableOn,
        lastTaken,
        completedAssessments,
        hasOnGoingAssessment,
        hasCompletedAssessment,
        handleGoToMyExperience,
        handleGoToMyPace,
        handleStartPace,
        handleGoToReport,
        paceStatusInfo,
    } = useMyDashboard();

    return (
        <>
            <Header backgroundColor="primary" sx={{ paddingTop: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        My Dashboard
                    </Typography>
                </Box>
            </Header>

            <Body sx={{ padding: 2 }}>
                <Box display="flex" alignItems="center">
                    {reportStatus === "fetching" && (
                        <Skeleton animation="wave" variant="rounded" sx={{ width: "100%", height: 225 }} />
                    )}
                    {reportStatus === "done" && (
                        <>
                            {hasCompletedAssessment && (
                                <StatusCard
                                    sx={{
                                        width: 306,
                                        minWidth: 266,
                                        height: 208,
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        marginRight: theme.spacing(2),
                                    }}
                                    header={
                                        <StatusCardHeader
                                            title={paceStatusInfo.statusLegend}
                                            icon={
                                                paceStatusInfo.paceStaleId === "RECENT"
                                                    ? icons.circledCheck
                                                    : icons.errorTriangle
                                            }
                                            backgroundColor={statusBarColor}
                                        />
                                    }
                                    body={
                                        <Box
                                            sx={(theme: Theme) => ({
                                                height: 168,
                                                backgroundColor: theme.palette.gradients.g02.n1,
                                                color: theme.palette.common.white,
                                                padding: theme.spacing(2),
                                            })}
                                        >
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <CustomIcon
                                                    icon={icons.paceIconOutlined}
                                                    sx={{ fontSize: 18, mr: 1 }}
                                                />
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        lineHeight: "24px",
                                                        lettterSpacing: "0.15px",
                                                    }}
                                                >
                                                    {intl.formatMessage({ id: "retestCard.title" })}
                                                </Typography>
                                            </Box>
                                            {lastTaken && (
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                                                    {intl.formatMessage({ id: "retestCard.lastTaken" })}{" "}
                                                    <FormattedDate
                                                        value={new Date(lastTaken)}
                                                        month="short"
                                                        day="2-digit"
                                                        year="numeric"
                                                    />
                                                </Typography>
                                            )}
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                                                {intl.formatMessage({ id: "retestCard.numberOfAssessments" })}{" "}
                                                {completedAssessments}
                                            </Typography>

                                            <Box display="flex" justifyContent="center">
                                                {!paceStatusInfo.isRetakeAvailable ? (
                                                    <Box display="flex" alignItems="center" mt={2}>
                                                        <CustomIcon
                                                            icon={icons.paceIconColouredDark}
                                                            sx={{ fontSize: 16, mr: 1 }}
                                                        />
                                                        <Typography
                                                            variant="body1"
                                                            fontSize={13}
                                                            letterSpacing="1.25px"
                                                        >
                                                            <FormattedMessage
                                                                id="retestCard.nextRetakeAvailable"
                                                                values={{ nextRetake: retakeAvailableOn }}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        fullWidth
                                                        sx={(theme: Theme) => ({
                                                            height: 32,
                                                            color: theme.palette.primary.dark,
                                                            background: theme.palette.common.white,
                                                            boxShadow: `0px 5px 7px 0px ${alpha(
                                                                theme.palette.common.white,
                                                                0.16
                                                            )}`,
                                                            "&:hover": {
                                                                background: theme.palette.common.white,
                                                            },
                                                        })}
                                                        onClick={handleStartPace}
                                                    >
                                                        <Box display="flex" alignItems="center">
                                                            <CustomIcon
                                                                icon={icons.paceIconColouredDark}
                                                                sx={{ fontSize: 16, mr: 1 }}
                                                            />
                                                            <Typography variant="body1" fontSize={13}>
                                                                {hasOnGoingAssessment
                                                                    ? intl.formatMessage({
                                                                          id: "retestCard.button.label.continue",
                                                                      })
                                                                    : intl.formatMessage({
                                                                          id: "retestCard.button.label",
                                                                      })}
                                                            </Typography>
                                                        </Box>
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            )}

                            <Box
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.background.paleBlue,
                                    borderRadius: "16px",
                                    padding: theme.spacing(2),
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                })}
                            >
                                <Typography variant="subtitle1" color="secondary.main" mb={2}>
                                    {intl.formatMessage({ id: "pace.myDashboard.welcomeText" })}
                                </Typography>
                                <Box display="flex" flex={1}>
                                    <LeadershipSnapshotCard
                                        title={
                                            hasCompletedAssessment
                                                ? "pace.myDashboard.snapshotCard1.title"
                                                : "pace.myDashboard.snapshotCard4.title"
                                        }
                                        icon={
                                            <CustomIcon
                                                icon={icons.paceIconColouredDark}
                                                sx={{ fontSize: 21, mr: 1 }}
                                            />
                                        }
                                        backgroundColor={
                                            hasCompletedAssessment
                                                ? theme.palette.background.paper
                                                : theme.palette.primary.dark
                                        }
                                        buttonLabel={
                                            hasCompletedAssessment
                                                ? "View Assessment"
                                                : hasOnGoingAssessment
                                                ? "Continue Assessment"
                                                : "Start Assessment Now"
                                        }
                                        color={
                                            hasCompletedAssessment
                                                ? theme.palette.text.primary
                                                : theme.palette.common.white
                                        }
                                        sx={{
                                            button: !hasCompletedAssessment
                                                ? {
                                                      color: theme.palette.common.white,
                                                      borderColor: theme.palette.common.white,
                                                  }
                                                : undefined,
                                        }}
                                        onClick={hasCompletedAssessment ? handleGoToReport : handleStartPace}
                                    />
                                    <LeadershipSnapshotCard
                                        title={
                                            hasCompletedAssessment
                                                ? "pace.myDashboard.snapshotCard2.title"
                                                : "pace.myDashboard.snapshotCard3.title"
                                        }
                                        icon={
                                            <CustomIcon
                                                icon={hasCompletedAssessment ? icons.sparkles : icons.crossHair}
                                                sx={{ fontSize: 20, mr: 1 }}
                                            />
                                        }
                                        backgroundColor={theme.palette.background.paper}
                                        buttonLabel="Go To My Experience"
                                        onClick={handleGoToMyExperience}
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* MAIN CONTAINER */}
                <Box display="flex" mt={2}>
                    <Box
                        width="65%"
                        onClick={hasCompletedAssessment ? handleGoToMyPace : undefined}
                        sx={{
                            mr: 2,
                            padding: theme.spacing(2, 4),
                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                            borderRadius: "6px",
                            "&:hover": {
                                cursor: hasCompletedAssessment ? "pointer" : "default",
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mt: 0, mb: 2 }}>
                                My Pace
                            </Typography>
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 10,
                                    color: theme.palette.common.white,
                                    backgroundColor: theme.palette.button.active.from,
                                }}
                            >
                                {completedAssessments}
                            </Box>
                        </Box>

                        {reportStatus === "fetching" || chartData.status === "fetching" ? (
                            <Skeleton animation="wave" variant="rounded" sx={{ height: 430 }} />
                        ) : (
                            <Box>
                                <BehaviouralBreakdown
                                    width={0}
                                    height={0}
                                    getData={(store: RootState) => chartData.data}
                                    moreOptions={{
                                        shouldShowPaceDefinitions: false,
                                        hideCustomizeLabels: true,
                                        hideExpandButtons: true,
                                        shouldShowExpandAll: false,
                                        hideLegend: true,
                                        exporting: false,
                                        fallbackCondition: !hasCompletedAssessment,
                                        fallbackComponent: <BBCompletePaceError callBack={handleStartPace} />,
                                    }}
                                />
                                {hasCompletedAssessment && (
                                    <Box display="flex">
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                flex: 1,
                                                width: "100%",
                                                maxWidth: 400,
                                                margin: "24px auto 8px",
                                                color: "text.primary",
                                            }}
                                            endIcon={<CustomIcon icon={icons.arrowRight} />}
                                            startIcon={
                                                <CustomIcon icon={icons.paceIconPrevious} sx={{ fontSize: 16 }} />
                                            }
                                            onClick={handleGoToMyPace}
                                        >
                                            {intl.formatMessage({ id: "pace.myDashboard.comparePace.button" })}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box width="35%">
                        <YourProfile
                            personId={person_id}
                            title={intl.formatMessage({ id: "pace.myDashboard.workHistory.title" })}
                            isEditable={false}
                        />
                    </Box>
                </Box>
            </Body>
        </>
    );
};

export default MyDashboard;
